import { useState, useEffect } from 'react';
import { 
  Search,   
  X,
    FileText,
    Save,
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import SearchableSelect from '../components/SearchableSelect';
import { getAllWarehouses } from '../utils/warehouseApi';
import { createPhysicalStockCount, updatePhysicalStockCount, getAllPhysicalStockCounts, deletePhysicalStockCount } from '../utils/physicalStockApi';
import type { Warehouse } from '../types/warehouse';
import type { PhysicalStockCount, CreatePhysicalStockCountRequest, PhysicalStockCountItem, PhysicalStockCountsResponse } from '../types/physicalStockCount';
import CountDetails from '../components/CountDetails';
import DecimalInput from '../components/DecimalInput';
import { blurActiveInputBeforeSave } from '../utils/numberInput';
import { toast } from '../utils/toast';
import { todayLocalIso } from '../utils/localDate';

const PhysicalStockManage = () => {
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

      // New state for physical stock count
  const [currentStockCount, setCurrentStockCount] = useState<PhysicalStockCount | null>(null);
  const [countDate, setCountDate] = useState(todayLocalIso());
  const [includeBatches, setIncludeBatches] = useState(true);
  const [isCreatingCount, setIsCreatingCount] = useState(false);
  const [isSavingCount, setIsSavingCount] = useState(false);
  const [countItems, setCountItems] = useState<{ [key:number]: number }>({});
  
  // State for recent counts
  const [recentCounts, setRecentCounts] = useState<any[]>([]);
  const [isLoadingCounts, setIsLoadingCounts] = useState(false);
  
  // State for showing count details
  const [selectedCountId, setSelectedCountId] = useState<number | null>(null);
  
  // State for delete functionality
  const [isDeletingCount, setIsDeletingCount] = useState(false);
  
  useEffect(() => {
    fetchWarehouses();
    fetchRecentCounts();
  }, []);

  const fetchWarehouses = async () => {
    try {
      const data = await getAllWarehouses();
      setWarehouses(data);
      // Set the first warehouse as default if available
      if (data.length > 0) {
        setSelectedWarehouseId(data[0].id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch warehouses');
    }
  };

  const fetchRecentCounts = async () => {
    try {
      setIsLoadingCounts(true);
      const response: PhysicalStockCountsResponse = await getAllPhysicalStockCounts();
      // Handle the paginated response structure
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        setRecentCounts(response.data.data);
      } else {
        setRecentCounts([]);
      }
    } catch (err) {
      console.error('Failed to fetch recent counts:', err);
      setRecentCounts([]);
    } finally {
      setIsLoadingCounts(false);
    }
  };

    const handleCreatePhysicalStockCount = async () => {
        if (!selectedWarehouseId || !countDate) return;

        setIsCreatingCount(true);
      setError(null);

        try {
            const requestData: CreatePhysicalStockCountRequest = {
                warehouse_id: selectedWarehouseId,
                count_date: countDate,
                include_batches: includeBatches,
            };

            const data = await createPhysicalStockCount(requestData);
            setCurrentStockCount(data);
            
            // Initialize count items with 0 values
            const initialCountItems: { [key: number]: number } = {};
            data.items.forEach((item: PhysicalStockCountItem) => {
                initialCountItems[item.id] = 0;
            });
            setCountItems(initialCountItems);
            
                         setIsCreatingCount(false);
             setError(null);
             // Refresh the recent counts list
             fetchRecentCounts();
         } catch (err) {
             toast.error(err instanceof Error ? err.message : 'Failed to create physical stock count');
         } finally {
             setIsCreatingCount(false);
         }
    };

    const cancelPhysicalStockCount = () => {
        setCurrentStockCount(null);
        setCountItems({});
        setCountDate(todayLocalIso());
        setIncludeBatches(true);
        setError(null);
    };

    const savePhysicalStockCount = async () => {
        blurActiveInputBeforeSave();
        if (!currentStockCount) return;

        setIsSavingCount(true);
        setError(null);

        try {
            // Prepare the items with updated counted quantities
            const updatedItems = Object.entries(countItems).map(([id, countedQuantity]) => ({
                id: parseInt(id, 10),
                counted_quantity: countedQuantity,
            }));

            const data = await updatePhysicalStockCount(currentStockCount.id, updatedItems);
            setCurrentStockCount(data);
            toast.success('Physical stock count saved.');
      } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to save physical stock count');
        } finally {
            setIsSavingCount(false);
        }
    };

      const updateCountItem = (itemId: number, countedQuantity: number) => {
    setCountItems(prev => ({
      ...prev,
      [itemId]: countedQuantity,
    }));
  };

  const handleViewCount = (countId: number) => {
    setSelectedCountId(countId);
  };

  const handleEditCount = (countId: number) => {
    setSelectedCountId(countId);
  };

  const closeCountDetails = () => {
    setSelectedCountId(null);
  };

  const handleCountUpdated = () => {
    // Refresh the recent counts list when a count is updated
    fetchRecentCounts();
  };

  const handleDeleteCount = async (countId: number) => {
    if (!window.confirm('Are you sure you want to delete this physical stock count? This action cannot be undone.')) {
      return;
    }

    setIsDeletingCount(true);

    try {
      await deletePhysicalStockCount(countId);
      fetchRecentCounts();
      toast.success('Physical stock count deleted successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete physical stock count';

      if (errorMessage.includes('Cannot delete completed count')) {
        toast.error('Cannot delete completed counts. Only draft counts can be deleted.');
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsDeletingCount(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-white shadow border-b border-gray-200">
          <div className="px-6 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-6">
              <div>
                  <h1 className="text-2xl font-bold text-gray-900">Physical Stock Management</h1>
                <p className="mt-1 text-sm text-gray-500">Manage and monitor your physical stock counts</p>
              </div>
                

              </div>
              
              {/* Search - Right Side */}
              <div className="w-full max-w-md">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="text"
                    placeholder="Search by product code, name, or description..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-slate-800 focus:border-slate-600 transition-all duration-200 shadow-sm bg-white"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Secondary Actions */}
            <div className="mt-4 flex flex-wrap items-center gap-3">

          </div>
          </div>
        </div>
        {/* Main Content */}
        <div className="flex-1 overflow-auto p-6">
          {/* Error Display */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-lg">
              {error}
            </div>
          )}

                    {/* Physical Stock Count Creation */}
                    {!currentStockCount && (
                        <div className="mb-6 bg-white p-6 rounded-lg shadow">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-900">Create Physical Stock Count</h2>
                                    <p className="text-sm text-gray-500">Start a new physical stock count for the selected warehouse</p>
                                </div>
                            </div>

                                                         <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                 <div>
                                     <label className="block text-sm font-medium text-gray-700 mb-2">
                                         Warehouse
            </label>
            <SearchableSelect
              value={selectedWarehouseId || 0}
              onChange={(warehouseId) => setSelectedWarehouseId(Number(warehouseId))}
              placeholder="Select Warehouse"
              searchPlaceholder="Search warehouses..."
              options={warehouses.map(warehouse => ({
                value: warehouse.id,
                label: warehouse.name,
                description: warehouse.code || undefined,
              }))}
            />
          </div>

                                 <div>
                                     <label className="block text-sm font-medium text-gray-700 mb-2">
                                         Count Date
                                     </label>
                                     <input
                                         type="date"
                                         value={countDate}
                                         onChange={(e) => setCountDate(e.target.value)}
                                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-800 focus:border-transparent"
                                     />
              </div>

                                 <div>
                                     <label className="block text-sm font-medium text-gray-700 mb-2">
                                         Include Batches
                                     </label>
                                     <select
                                         value={includeBatches ? "true" : "false"}
                                         onChange={(e) => setIncludeBatches(e.target.value === "true")}
                                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-800 focus:border-transparent"
                                     >
                                         <option value="true">Yes</option>
                                         <option value="false">No</option>
                                     </select>
              </div>

                                 <div className="flex items-end">
                                     <button
                                         onClick={handleCreatePhysicalStockCount}
                                         disabled={isCreatingCount || !selectedWarehouseId || !countDate}
                                         className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg text-white bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-800 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                                     >
                                         {isCreatingCount ? (
                                             <>
                                                 <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                                 Creating...
                                             </>
                                         ) : (
                                             <>
                                                 <FileText className="h-4 w-4 mr-2" />
                                                 Start Count
                                             </>
                                         )}
                                     </button>
                </div>
              </div>
            </div>
          )}

                    {/* Physical Stock Count Form */}
                    {currentStockCount && (
                        <div className="mb-6 bg-white p-6 rounded-lg shadow">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-900">Physical Stock Count</h2>
                                    <p className="text-sm text-gray-500">
                                        Count Date: {currentStockCount.count_date} | Status: {currentStockCount.status}
                                    </p>
                                    {currentStockCount.warehouse && (
                                        <p className="text-sm text-gray-500">
                                            Warehouse: {currentStockCount.warehouse.name}
                                        </p>
                                    )}
                                    {currentStockCount.creator && (
                                        <p className="text-sm text-gray-500">
                                            Created by: {currentStockCount.creator.name}
                                        </p>
                                    )}
                                </div>
                                <div className="flex space-x-2">
                                    <button
                                        onClick={() => {
                                            const expectedCounts: { [key: number]: number } = {};
                                            currentStockCount.items.forEach(item => {
                                                expectedCounts[item.id] = typeof item.expected_quantity === 'string' ? parseFloat(item.expected_quantity) : item.expected_quantity;
                                            });
                                            setCountItems(expectedCounts);
                                        }}
                                        className="px-4 py-2 text-sm font-medium rounded-lg text-slate-700 bg-slate-100 hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-800 transition-colors duration-150"
                                        title="Set all quantities to expected values"
                                    >
                                        Use Expected
                                    </button>
                                    <button
                                        onClick={cancelPhysicalStockCount}
                                        className="px-4 py-2 text-sm font-medium rounded-lg text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-150"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={savePhysicalStockCount}
                                        disabled={isSavingCount}
                                        className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg text-white bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-800 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isSavingCount ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <Save className="h-4 w-4 mr-2" />
                                                Save Count
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>

                            {/* Count Summary */}
                            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                    <div>
                                        <div className="text-sm font-medium text-gray-500">Total Items</div>
                                        <div className="text-lg font-semibold text-gray-900">{currentStockCount.items.length}</div>
                                    </div>
                                    <div>
                                        <div className="text-sm font-medium text-gray-500">Expected Total</div>
                                        <div className="text-lg font-semibold text-gray-900">
                                            {currentStockCount.items.reduce((sum, item) => sum + (typeof item.expected_quantity === 'string' ? parseFloat(item.expected_quantity) : item.expected_quantity), 0).toFixed(2)}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-sm font-medium text-gray-500">Counted Total</div>
                                        <div className="text-lg font-semibold text-gray-900">
                                            {Object.values(countItems).reduce((sum, qty) => sum + (qty || 0), 0).toFixed(2)}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-sm font-medium text-gray-500">Overall Variance</div>
                                        <div className={`text-lg font-semibold ${
                                            Object.values(countItems).reduce((sum, qty) => sum + (qty || 0), 0) - 
                                            currentStockCount.items.reduce((sum, item) => sum + (typeof item.expected_quantity === 'string' ? parseFloat(item.expected_quantity) : item.expected_quantity), 0) === 0
                                                ? 'text-green-600'
                                                : Object.values(countItems).reduce((sum, qty) => sum + (qty || 0), 0) - 
                                                  currentStockCount.items.reduce((sum, item) => sum + (typeof item.expected_quantity === 'string' ? parseFloat(item.expected_quantity) : item.expected_quantity), 0) > 0
                                                    ? 'text-slate-700'
                                                    : 'text-red-600'
                                        }`}>
                                            {(Object.values(countItems).reduce((sum, qty) => sum + (qty || 0), 0) - 
                                              currentStockCount.items.reduce((sum, item) => sum + (typeof item.expected_quantity === 'string' ? parseFloat(item.expected_quantity) : item.expected_quantity), 0)).toFixed(2)}
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Progress Bar */}
                                <div className="mt-4">
                                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                                        <span>Counting Progress</span>
                                        <span>
                                            {Object.keys(countItems).filter(key => (countItems[parseInt(key)] || 0) > 0).length} / {currentStockCount.items.length} items counted
                                        </span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div 
                                            className="bg-slate-800 h-2 rounded-full transition-all duration-300"
                                            style={{ 
                                                width: `${(Object.keys(countItems).filter(key => (countItems[parseInt(key)] || 0) > 0).length / currentStockCount.items.length) * 100}%` 
                                            }}
                                        ></div>
                                    </div>
                                </div>
                            </div>

                            {/* Count Items Table */}
                            <div className="overflow-x-auto">
                <table className="w-full min-w-[1000px] divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Product
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Batch
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Expected Quantity
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Counted Quantity
                      </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Variance
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Counted By
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                                        {currentStockCount.items.map((item: any) => (
                                            <tr key={item.id} className={`hover:bg-gray-50 ${
                                                (countItems[item.id] || 0) > 0 ? 'bg-green-50' : ''
                                            }`}>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {item.product?.name || `Product ${item.product_id}`}
                                                        </div>
                                                        <div className="text-sm text-gray-500">
                                                            {item.product?.code || 'N/A'}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {item.batch?.batch_number || `Batch ${item.batch_id}`}
                                                        </div>
                                                        <div className="text-xs text-gray-500">
                                                            Batch Qty: {(typeof item.batch?.quantity === 'string' ? parseFloat(item.batch.quantity) : item.batch?.quantity)?.toFixed(2) || 'N/A'}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {(typeof item.expected_quantity === 'string' ? parseFloat(item.expected_quantity) : item.expected_quantity).toFixed(2)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                                    <DecimalInput
                                                        value={countItems[item.id] || 0}
                                                        onCommit={(n) => updateCountItem(item.id, n)}
                                                        min={0}
                                                        className="w-24 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-800 focus:border-transparent"
                                                    />
                            </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                    <span className={`font-medium ${(countItems[item.id] || 0) - (typeof item.expected_quantity === 'string' ? parseFloat(item.expected_quantity) : item.expected_quantity) === 0
                                                            ? 'text-green-600'
                                                            : (countItems[item.id] || 0) - (typeof item.expected_quantity === 'string' ? parseFloat(item.expected_quantity) : item.expected_quantity) > 0
                                                                ? 'text-slate-700'
                                                                : 'text-red-600'
                                                        }`}>
                                                        {((countItems[item.id] || 0) - (typeof item.expected_quantity === 'string' ? parseFloat(item.expected_quantity) : item.expected_quantity)).toFixed(2)}
                                  </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center space-x-2">
                                    <div className="text-sm text-gray-500">
                                        Not specified
                                    </div>
                                    {(countItems[item.id] || 0) > 0 && (
                                        <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                                            Counted
                                        </span>
                                    )}
                                </div>
                            </td>
                          </tr>
                                        ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

                                         {/* Recent Physical Stock Counts */}
                     <div className="bg-white rounded-lg shadow">
                         <div className="px-6 py-4 border-b border-gray-200">
                             <h3 className="text-lg font-semibold text-gray-900">Recent Physical Stock Counts</h3>
                             <p className="text-sm text-gray-500">View and manage your recent stock count activities</p>
                         </div>
                         
                         <div className="p-6">
                             {isLoadingCounts ? (
                                 <div className="flex items-center justify-center py-8">
                                     <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-800"></div>
                                 </div>
                             ) : recentCounts.length === 0 ? (
                                 <div className="text-center py-8">
                                     <div className="text-gray-400 mb-2">
                                         <FileText className="h-12 w-12 mx-auto" />
                                     </div>
                                     <p className="text-gray-500">No physical stock counts found</p>
                                     <p className="text-sm text-gray-400">Create your first count to get started</p>
                                 </div>
                             ) : (
                                 <div className="overflow-x-auto">
                                     <table className="w-full min-w-[800px] divide-y divide-gray-200">
                                         <thead className="bg-gray-50">
                                             <tr>
                                                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                     Count ID
                                                 </th>
                                                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                     Warehouse
                                                 </th>
                                                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                     Count Date
                                                 </th>
                                                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                     Status
                                                 </th>
                                                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                     Created By
                                                 </th>
                                                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                     Actions
                                                 </th>
                                             </tr>
                                         </thead>
                                         <tbody className="bg-white divide-y divide-gray-200">
                                             {recentCounts.map((count) => (
                                                 <tr key={count.id} className="hover:bg-gray-50">
                                                     <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                         #{count.id}
                                                     </td>
                                                     <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                         {count.warehouse?.name || `Warehouse ${count.warehouse_id}`}
                                                     </td>
                                                     <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                         {new Date(count.count_date).toLocaleDateString('en-US', {
                                                             year: 'numeric',
                                                             month: 'short',
                                                             day: 'numeric'
                                                         })}
                                                     </td>
                                                     <td className="px-6 py-4 whitespace-nowrap">
                                                         <div className="flex items-center space-x-2">
                                                             <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                                                 count.status === 'completed' ? 'bg-green-100 text-green-800' :
                                                                 count.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                                                                 count.status === 'in_progress' ? 'bg-slate-100 text-slate-800' :
                                                                 'bg-gray-100 text-gray-800'
                                                             }`}>
                                                                 {count.status.replace('_', ' ').toUpperCase()}
                                                             </span>
                                                             {count.status === 'draft' && (
                                                                 <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                                                                     Deletable
                                                                 </span>
                                                             )}
                                                         </div>
                                                     </td>
                                                     <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                         {count.creator?.name || `User ${count.created_by}`}
                                                     </td>
                                                     <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                         <button
                                                             onClick={() => handleViewCount(count.id)}
                                                             className="text-slate-700 hover:text-slate-900 mr-3"
                                                         >
                                                             View
                                                         </button>
                                                         <button
                                                             onClick={() => handleEditCount(count.id)}
                                                             className="text-green-600 hover:text-green-900 mr-3"
                                                         >
                                                             Edit
                                                         </button>
                                                         {count.status === 'draft' && (
                                                             <button
                                                                 onClick={() => handleDeleteCount(count.id)}
                                                                 disabled={isDeletingCount}
                                                                 className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                 title="Delete draft count"
                                                             >
                                                                 {isDeletingCount ? (
                                                                     <>
                                                                         <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600 mr-1"></div>
                                                                         Deleting...
                                                                     </>
                                                                 ) : (
                                                                     'Delete'
                                                                 )}
                                                             </button>
                                                         )}
                                                     </td>
                                                 </tr>
                                             ))}
                                         </tbody>
                                     </table>
                                 </div>
                             )}
                         </div>
                     </div>

                     {/* Count Details Component */}
                     {selectedCountId && (
                         <CountDetails
                             countId={selectedCountId}
                             onClose={closeCountDetails}
                             onCountUpdated={handleCountUpdated}
                         />
                     )}
                 </div>
             </div>
         </div>
     );
 };

export default PhysicalStockManage;
